/**
 * Weather settings section for the V2 site settings page.
 *
 * Surfaces the WEATHER feature enable/disable toggle and, when the feature is
 * effectively enabled, the per-site weather configuration fields
 * (weather_sensitivity, relays, invert_operation).
 *
 * The section appears when the site-settings GET returns a `weather` key (BE
 * gate) or when the snapshot shows WEATHER is effectively enabled. Config
 * fields are wired into the parent Formik context under the `weather` key so
 * the existing Save button includes `{"weather": {...}}` in the PATCH.
 *
 * The feature toggle calls upsertSiteFeatureAssociation (new odl-app PATCH
 * endpoint) directly — it does NOT go through the Formik save flow — and
 * re-fetches site settings on enable so the weather config section appears.
 *
 * Contains:
 * - WeatherSection — the SettingsSection component to mount in SiteSettingsViewV2
 */

import CloudIcon from '@mui/icons-material/Cloud';
import {
  Box,
  FormControl,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useField } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectSiteFeatureAssociationByKey,
  siteFeatureRequiresCommandLease,
  useUpsertSiteFeatureAssociationMutation,
} from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';

import { SettingsFieldRow, SettingsSection, SettingsSubsection } from './index';

const RELAY_OPTIONS = [1, 2, 3, 4];

// Numbered relay toggle buttons 1–4 (multi-select).
function RelaysToggleField({ name, disabled }) {
  const { t } = useTranslation();
  const [field, , helpers] = useField(name);
  const value = field.value ?? [];

  const handleChange = (_event, newValues) => {
    helpers.setValue(newValues);
  };

  return (
    <FormControl component="fieldset" disabled={disabled} sx={{ width: '100%', alignItems: 'center' }}>
      <ToggleButtonGroup
        value={value}
        onChange={handleChange}
        aria-label={t('weather.relays', 'Relays')}
        disabled={disabled}
      >
        {RELAY_OPTIONS.map(n => (
          <ToggleButton
            key={n}
            value={n}
            aria-label={`relay-${n}`}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            {n}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormControl>
  );
}

// Sensitivity slider 0–100 dBZ.
function SensitivityField({ name, disabled }) {
  const { t } = useTranslation();
  const [field, , helpers] = useField(name);
  const value = field.value ?? 50;

  return (
    <Box sx={{ px: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Slider
          min={0}
          max={100}
          step={1}
          value={value}
          onChange={(_e, v) => helpers.setValue(v)}
          disabled={disabled}
          aria-label={t('weather.sensitivity', 'Sensitivity')}
          valueLabelDisplay="auto"
          sx={{ flex: 1 }}
        />
        <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ minWidth: 32 }}>
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}

// Invert operation switch.
function InvertField({ name, disabled }) {
  const { t } = useTranslation();
  const [field, , helpers] = useField(name);
  return (
    <FormControlLabel
      label={t('weather.invertOperation', 'Turn lights OFF in bad weather')}
      control={
        <Switch
          checked={Boolean(field.value)}
          onChange={(_e, checked) => helpers.setValue(checked)}
          disabled={disabled}
        />
      }
    />
  );
}

/**
 * Weather section for the V2 site settings page.
 *
 * @param {{ siteId: string|number, disabled: boolean }} props
 */
export default function WeatherSection({ siteId, disabled }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [upsertSiteFeatureAssociation] = useUpsertSiteFeatureAssociationMutation();
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);

  const weatherAssociation = useSelector(selectSiteFeatureAssociationByKey(siteId, 'WEATHER'));
  const isEffectivelyEnabled = weatherAssociation?.is_effectively_enabled ?? false;
  const isDisabledFromParent = weatherAssociation?.is_disabled_from_parent ?? false;

  // Config fields from the parent Formik context (weather section).
  const [weatherField] = useField({ name: 'weather' });
  const hasWeatherConfig = weatherField.value != null;

  // The settings GET returns the weather section only when WEATHER is
  // effectively enabled, so hasWeatherConfig is a reliable "on" signal even on
  // the settings route — which doesn't load the dashboard snapshot that
  // is_effectively_enabled comes from (so weatherAssociation is null here).
  const isOn = isEffectivelyEnabled || hasWeatherConfig;

  // Show config fields when the feature is on (either signal).
  const showConfig = isOn;

  const [toggling, setToggling] = React.useState(false);
  const requiresCommand = siteFeatureRequiresCommandLease('WEATHER', !isOn);

  const handleToggle = React.useCallback(async () => {
    setToggling(true);
    try {
      const outcome = await runWrite(
        () =>
          upsertSiteFeatureAssociation({
            siteId,
            feature_key: 'WEATHER',
            is_enabled: !isOn,
          }).unwrap(),
        { requiresCommand },
      );
      if (!outcome.executed) return;
      // markStaleSiteSettings is NOT called here — the view-level
      // weatherEffectiveRef effect fires on the is_effectively_enabled
      // change that upsertSiteFeatureAssociation writes to the snapshot,
      // ensuring exactly one settings refetch per toggle.
    } catch {
      dispatch(
        addToast({
          title: t('general.error', 'Error'),
          message: t('weather.failedToggle', 'Failed to update the WEATHER feature.'),
          level: 'error',
          dismissible: true,
        }),
      );
    } finally {
      setToggling(false);
    }
  }, [dispatch, isOn, requiresCommand, runWrite, siteId, t, upsertSiteFeatureAssociation]);

  return (
    <SettingsSection
      id="settings-weather"
      icon={<CloudIcon />}
      title={t('siteSettings.v2.weather', 'Weather')}
      description={t(
        'siteSettings.v2.weatherDescription',
        'Automatic relay control based on local weather conditions.',
      )}
    >
      <SettingsSubsection title={t('siteSettings.v2.weatherFeature', 'Feature')} bordered>
        <SettingsFieldRow
          label={t('weather.weatherEnabled', 'Weather Enabled')}
          description={
            isDisabledFromParent
              ? t('weather.disabledFromParent', 'WEATHER is disabled at the site group level.')
              : t('weather.featureDescription', 'Allow weather conditions to control relays at this site.')
          }
          controlWidth={80}
        >
          <Switch
            checked={isOn}
            onChange={handleToggle}
            disabled={toggling || isDisabledFromParent || disabled || (requiresCommand && commandLocked)}
          />
        </SettingsFieldRow>
      </SettingsSubsection>

      {showConfig ? (
        <SettingsSubsection
          title={t('siteSettings.v2.weatherConfig', 'Configuration')}
          description={t('siteSettings.v2.weatherConfigDescription', 'Changes are saved with the site Save button.')}
          bordered
        >
          <Stack spacing={3}>
            <SettingsFieldRow
              label={t('weather.sensitivity', 'Sensitivity')}
              description={t(
                'weather.sensitivityDescription',
                'Radar reflectivity threshold (dBZ) above which the weather system activates.',
              )}
              stacked
            >
              <SensitivityField name="weather.weather_sensitivity" disabled={disabled} />
            </SettingsFieldRow>

            <SettingsFieldRow
              label={t('weather.relays', 'Controlled Relays')}
              description={t('weather.relaysDescription', 'Which relays the weather system has authority over.')}
              controlWidth={280}
            >
              <RelaysToggleField name="weather.relays" disabled={disabled} />
            </SettingsFieldRow>

            <SettingsFieldRow
              label={t('weather.invertOperation', 'Invert Operation')}
              description={t(
                'weather.invertDescription',
                'When on, the weather system turns lights OFF during bad weather instead of on.',
              )}
              controlWidth={340}
            >
              <InvertField name="weather.invert_operation" disabled={disabled} />
            </SettingsFieldRow>
          </Stack>
        </SettingsSubsection>
      ) : null}
    </SettingsSection>
  );
}
